import { useCallback, useRef, useState } from 'react';
import { UploadCloud, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TOOL_PAGE } from '@/constants/testIds';
import { humanBytes } from '@/lib/download';

export function UploadDropzone({
    accept,
    onFile,
    file,
    onClear,
    disabled = false,
    hint,
}) {
    const inputRef = useRef(null);
    const [isDragging, setDragging] = useState(false);

    const openPicker = useCallback(() => {
        if (disabled) return;
        inputRef.current?.click();
    }, [disabled]);

    const onDrop = useCallback(
        (e) => {
            e.preventDefault();
            setDragging(false);
            if (disabled) return;
            const f = e.dataTransfer.files?.[0];
            if (f) onFile(f);
        },
        [disabled, onFile],
    );

    return (
        <div
            className={`rounded-xl border-2 border-dashed transition-colors duration-150 ${
                isDragging
                    ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]/60'
                    : 'border-border bg-white hover:border-slate-300'
            }`}
            onDragOver={(e) => {
                e.preventDefault();
                if (!disabled) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            data-testid={TOOL_PAGE.dropzone}
        >
            {file ? (
                <div className="p-5 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{humanBytes(file.size)}</p>
                    </div>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onClear}
                        disabled={disabled}
                        data-testid={TOOL_PAGE.resetButton}
                        className="text-slate-600 hover:text-slate-900"
                    >
                        <X className="w-4 h-4 mr-1" /> Remove
                    </Button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={openPicker}
                    className="w-full p-8 flex flex-col items-center justify-center gap-3 text-center"
                    aria-label="Upload a file"
                >
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <UploadCloud className="w-6 h-6" />
                    </span>
                    <div>
                        <p className="text-sm font-medium text-slate-900">
                            Drop your file here, or click to browse
                        </p>
                        <p className="text-xs text-slate-500 mt-1">{hint}</p>
                    </div>
                </button>
            )}
            <input
                ref={inputRef}
                type="file"
                className="hidden"
                accept={accept}
                data-testid={TOOL_PAGE.fileInput}
                onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFile(f);
                    // Reset value so choosing the same file again re-triggers
                    e.target.value = '';
                }}
            />
        </div>
    );
}
